import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

export interface FitTextProps {
  children: string
  /**
   * What to draw for `children` when it is more than plain text but reads as
   * exactly that string (a glossed verbatim's muted spans, `glossedQuote`).
   * The fit always measures `children`.
   */
  rendered?: React.ReactNode
  /** Max lines the text may occupy. Font shrinks until it fits this many lines. */
  maxLines?: number
  /** Font size to use when the text already fits (px). */
  baseFontSize?: number
  /** Never shrink below this (px). If text still overflows here, a line-clamp backstops it. */
  minFontSize?: number
  /** Unitless line-height multiple. */
  lineHeight?: number
  className?: string
  style?: React.CSSProperties
  as?: keyof JSX.IntrinsicElements
  'data-testid'?: string
}

/**
 * Auto-fitting text: keeps `children` at `baseFontSize` when it fits `maxLines`,
 * otherwise shrinks the font (down to `minFontSize`) until it does. This guarantees
 * no visible cropping for arbitrary title length AND arbitrary brand font, without
 * truncating the words. Measurement uses a hidden clone with the element's real
 * computed font, and re-runs on webfont load + container resize so the headless
 * PDF render (which waits for `document.fonts.ready`) captures the fitted size.
 */
export function FitText({
  children,
  maxLines = 3,
  baseFontSize = 40,
  minFontSize = 24,
  lineHeight = 1.2,
  className,
  style,
  as = 'div',
  'data-testid': dataTestId,
  rendered,
}: FitTextProps) {
  const ref = useRef<HTMLElement | null>(null)
  const [fontSize, setFontSize] = useState(baseFontSize)
  const Tag = as as any

  const fit = useCallback(() => {
    const el = ref.current
    if (!el || typeof document === 'undefined') return
    const width = el.clientWidth
    if (!width) return
    const cs = window.getComputedStyle(el)
    const clone = document.createElement(as)
    clone.textContent = children
    Object.assign(clone.style, {
      position: 'absolute',
      left: '-99999px',
      top: '0',
      visibility: 'hidden',
      width: `${width}px`,
      margin: '0',
      padding: '0',
      fontFamily: cs.fontFamily,
      fontWeight: cs.fontWeight,
      fontStyle: cs.fontStyle,
      letterSpacing: cs.letterSpacing,
      textTransform: cs.textTransform,
      whiteSpace: 'normal',
      wordBreak: cs.wordBreak,
      lineHeight: String(lineHeight),
    } as Partial<CSSStyleDeclaration>)
    // Match the rendered element's wrapping (e.g. text-wrap: balance); otherwise the
    // measured line count can differ from what actually renders.
    ;(clone.style as any).textWrap = (cs as any).textWrap || ''
    document.body.appendChild(clone)
    let best = minFontSize
    try {
      const fitsAt = (fs: number) => {
        clone.style.fontSize = `${fs}px`
        // `getBoundingClientRect().height`, NOT `scrollHeight`. scrollHeight is
        // the scrollable content box, which includes glyph overflow: a font
        // whose ascent+descent exceed the CSS line box reports far more than
        // the text occupies. Measured on the deck's own display face (Sumana)
        // at 24px with lineHeight 1.2, one line is 28.8px tall and scrollHeight
        // returns 49 -- a 70% over-estimate. Every headline was therefore
        // shrunk harder than it needed to be, and long ones bottomed out at
        // minFontSize when they had room to spare.
        return clone.getBoundingClientRect().height <= fs * lineHeight * maxLines + 1
      }
      let lo = minFontSize
      let hi = baseFontSize
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2)
        if (fitsAt(mid)) {
          best = mid
          lo = mid + 1
        } else {
          hi = mid - 1
        }
      }
    } finally {
      document.body.removeChild(clone)
    }
    setFontSize((prev) => (prev === best ? prev : best))
  }, [as, children, maxLines, baseFontSize, minFontSize, lineHeight])

  useLayoutEffect(() => {
    fit()
  }, [fit])

  useEffect(() => {
    let cancelled = false
    const rerun = () => {
      if (!cancelled) fit()
    }
    // Re-fit once webfonts finish loading (metrics change vs the fallback font).
    const fonts = (document as any).fonts
    if (fonts && typeof fonts.ready?.then === 'function') {
      fonts.ready.then(rerun).catch(() => {})
    }
    let ro: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined' && ref.current) {
      ro = new ResizeObserver(rerun)
      ro.observe(ref.current)
    }
    return () => {
      cancelled = true
      ro?.disconnect()
    }
  }, [fit])

  return (
    <Tag
      ref={ref as any}
      data-testid={dataTestId}
      className={cn(className)}
      style={{
        fontSize,
        lineHeight,
        // `flexShrink: 0` keeps the element at its content height so a flex column can't
        // shrink the title below its own content. For in-budget titles this equals the
        // natural height and none of the clamping below has any effect.
        flexShrink: 0,
        maxHeight: `${(maxLines * fontSize * lineHeight).toFixed(3)}px`,
        overflow: 'hidden',
        // The backstop, for a title too long to fit `maxLines` even at
        // `minFontSize`. This used to be `maxHeight` + `overflow: hidden` alone,
        // which cuts the text mid-sentence with no ellipsis and no way to see
        // the rest -- the "text cut in the middle of a slide" QA kept reporting.
        // The prop doc above already promised "a line-clamp backstops it"; it
        // was never implemented. `-webkit-line-clamp` is what makes the browser
        // draw the ellipsis, and it is the same mechanism `TextClamp` already
        // uses for every other clamped field on a slide.
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: maxLines,
        ...style,
      }}
    >
      {rendered ?? children}
    </Tag>
  )
}
